import Quill from 'quill';
// Enable Quill internal logging to help trace module activity
try { (Quill as any).debug && (Quill as any).debug('info'); } catch {}
// Ensure mention module is registered before any Quill editor initializes
import  * as QuillMention from 'quill-mention';
// Some Angular builds may load different Quill copies. Robustly register the module
try {
  const MentionModule: any = (QuillMention as any)?.default || (QuillMention as any);
  if (MentionModule) {
    (Quill as any).register('modules/mention', MentionModule, true);
  }
} catch (e) {
  // Fallback to side-effect import style if direct register fails
  try { require('quill-mention'); } catch {}
}

// Some components set editorConfig.enableTable = true, which makes Quill try to import
// a 'modules/enableTable' module. If not present, Quill logs an error. Register a no-op
// module so editors initialize cleanly without extra dependencies.
try {
  class EnableTableModule {
    constructor(_quill: any, _options: any) { /* no-op */ }
  }
  (Quill as any).register('modules/enableTable', EnableTableModule, true);
} catch {}

// Inline embed for Due Date that preserves exact HTML structure
// This avoids Quill clipboard sanitization when inserting HTML
try {
  const Embed: any = Quill.import('blots/embed');
  class DateBlot extends Embed {
    static blotName = 'date';
    static tagName = 'SPAN';
    static className = 'minDateCls';

    static create(value: any) {
      const node: HTMLElement = super.create() as any;
      // node.setAttribute('contenteditable', 'false');
      // DO NOT set contenteditable="false" - it blocks cursor after the element
      try { if (value?.code) node.setAttribute('code', String(value.code)); } catch {}
      // value.text is expected to contain "M dd, yy" plus optional timezone span HTML
      const textHtml = (value && value.text) ? String(value.text) : '';
      node.innerHTML = `<span class='dueLabelCls'>Due Date: </span>${textHtml}`;
      return node;
    }

    static value(node: HTMLElement) {
      try {
        const code = node.getAttribute('code') || '';
        const text = node.innerHTML.replace(/<span[^>]*class=['"]dueLabelCls['"][^>]*>[^<]*<\/span>/i, '').trim();
        return { code, text };
      } catch {
        return {};
      }
    }
  }
  Quill.register(DateBlot);
} catch {}

// Inline embed for Agenda Duration that preserves exact HTML structure
// This supports inserting/selecting duration chips without clipboard sanitization
try {
  const Embed: any = Quill.import('blots/embed');
  class DurationBlot extends Embed {
    static blotName = 'duration';
    static tagName = 'SPAN';
    static className = 'agDurCls';

    static create(value: any) {
      const node: HTMLElement = super.create() as any;
      // keep chip non-editable like legacy
      // node.setAttribute('contenteditable', 'false');
      const minutes = (value && (value.minutes != null)) ? String(value.minutes) : '0';
      // Legacy inner HTML: [ {minutes} Minutes ] using agDurLabelCls
      node.innerHTML = `<span class='agDurLabelCls'>[ </span>${minutes}<span class='agDurLabelCls'> Minutes ]</span>`;
      try { node.setAttribute('data-minutes', String(minutes)); } catch {}
      return node;
    }

    static value(node: HTMLElement) {
      try {
        const data = node.getAttribute('data-minutes');
        const minutes = data != null ? Number(data) : undefined;
        return { minutes };
      } catch {
        return {};
      }
    }
  }
  Quill.register(DurationBlot);
} catch {}

// Owner label embed (Presented by) for agenda
try {
  const Embed: any = Quill.import('blots/embed');
  class OwnerLabelBlot extends Embed {
    static blotName = 'ownerlabel';
    static tagName = 'SPAN';
    static className = 'presentedByCls';

    static create(_value: any) {
      const node: HTMLElement = super.create() as any;
      node.setAttribute('contenteditable', 'false');
      node.innerHTML = 'Presented by&nbsp;';
      return node;
    }

    static value(_node: HTMLElement) {
      return {};
    }
  }
  Quill.register(OwnerLabelBlot);
} catch {}

// Task owner label embed (Owner(s):) for tasks
try {
  const Embed: any = Quill.import('blots/embed');
  class TaskOwnerLabelBlot extends Embed {
    static blotName = 'taskownerlabel';
    static tagName = 'SPAN';
    static className = 'assignCls';

    static create(_value: any) {
      const node: HTMLElement = super.create() as any;
      node.setAttribute('contenteditable', 'false');
      node.textContent = 'Owner(s): ';
      return node;
    }

    static value(_node: HTMLElement) {
      return {};
    }
  }
  Quill.register(TaskOwnerLabelBlot);
} catch {}

// Legacy atwho mention embed to preserve fkuserid/id attributes and email
try {
  const Embed: any = Quill.import('blots/embed');
  class AtwhoMentionBlot extends Embed {
    static blotName = 'atwhomention';
    static tagName = 'SPAN';
    static className = 'atwho-inserted';

    static create(value: any) {
      const node: HTMLElement = super.create() as any;
      const userId = value?.userId != null ? String(value.userId) : '';
      const name = value?.name != null ? String(value.name) : '';
      const email = value?.email != null ? String(value.email) : '';
      node.setAttribute('fkuserid', userId);
      node.setAttribute('id', userId);
      node.setAttribute('contenteditable', 'false');
      node.setAttribute('data-atwho-at-query', '@');
      if (value?.isexternal !== undefined) {
        node.setAttribute('isexternal', String(value.isexternal));
      }
      if (email) {
        node.setAttribute('email', email);
      }
      node.textContent = name ? ` ${name}` : '';
      return node;
    }

    static value(node: HTMLElement) {
      try {
        const userId = node.getAttribute('fkuserid') || node.getAttribute('id') || '';
        const name = (node.textContent || '').trim();
        const isexternal = node.getAttribute('isexternal') || '';
        const email = node.getAttribute('email') || '';
        return { userId, name, isexternal, email };
      } catch {
        return {};
      }
    }
  }
  Quill.register(AtwhoMentionBlot);
} catch {}

try {
  const Embed: any = Quill.import('blots/embed');

  class AssignLabelBlot extends Embed {
    static blotName = 'assignlabel';
    static tagName = 'SPAN';
    static className = 'assignCls';

    static create(value: any) {
      const node: HTMLElement = super.create();
      node.innerHTML = 'Owner(s): ';
      return node;
    }

    static value(node: HTMLElement) {
      return { text: node.innerText };
    }
  }

  Quill.register(AssignLabelBlot);
} catch {}

try {
  const Embed: any = Quill.import('blots/embed');

  class AssignUserBlot extends Embed {
    static blotName = 'assignuser';
    static tagName = 'SPAN';
    static className = 'atwho-inserted';

    static create(value: any) {
      const node: HTMLElement = super.create();

      if (value?.id) node.setAttribute('id', String(value.id));
      if (value?.fkuserid) node.setAttribute('fkuserid', String(value.fkuserid));
      if (value?.email) node.setAttribute('email', String(value.email));
      if (value?.isexternal !== undefined)
        node.setAttribute('isexternal', String(value.isexternal));

      const safeName = value?.name ? String(value.name) : '';

      node.innerHTML = safeName;

      return node;
    }

    static value(node: HTMLElement) {
      return {
        id: node.getAttribute('id') || '',
        fkuserid: node.getAttribute('fkuserid') || '',
        email: node.getAttribute('email') || '',
        isexternal: node.getAttribute('isexternal') || '',
        name: node.innerText || ''
      };
    }
  }

  Quill.register(AssignUserBlot);
} catch {}



export type QuillModules = {
  toolbar: any[] | false | string;
  [key: string]: any;
};

export function buildQuillModules(_meetingResponse?: any): QuillModules {
  // Provide an inline toolbar config so Quill can initialize without needing an existing DOM selector.
  // The shared toolbar manager will relocate this toolbar into #ckEditBar on editor ready/focus.
  return {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '+1' }, { indent: '-1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: ['poppins', 'calibri'] }],
      [{ align: [] }],
      ['clean'],
      [{ table: true }],
      ['link', 'image', 'video'],
      ['insertheaderorfooter', 'subreport', 'preview', 'print', 'formula'],
    ],
    clipboard: {
      matchVisual: false,
    },
    // Ensure mention module is instantiated so component code can fetch and override it
    mention: {
      mentionDenotationChars: ['@'],
      minChars: 0,
      isolateCharacter: false,
      showDenotationChar: true,
      source: (searchTerm: string, renderList: Function) => {
        try { /* default no-op to instantiate module; components override at runtime */ } catch {}
        renderList([], searchTerm);
      }
    },
  } as QuillModules;
}


// src/app/config/quill-common.ts
// Global registry: maps Quill instances to their configs (for image upload)
const quillUploadConfigMap = new WeakMap<any, any>();

export function setupQuillUploadAdapter(
  quill: any,
  apiBaseUrl: string,
  attachmentEndpoint: string,
  meetingId: any,
  agendaId: any,
  onComplete?: (url: string) => void
) {
  // Store this quill's config in global registry
  quillUploadConfigMap.set(quill, {
    quill,
    apiBaseUrl,
    attachmentEndpoint,
    meetingId,
    agendaId,
    onComplete
  });

  const toolbar = quill.getModule('toolbar');
  if (!toolbar) return;

  // Register handler ONCE per toolbar - use a shared handler that routes to active editor
  toolbar.addHandler('image', () => {
    // Get the currently active Quill editor from the shared toolbar manager
    // This ensures we use the editor that the user is currently focused on
    const activeQuill = (window as any).__activeQuillEditor || quill;
    const config = quillUploadConfigMap.get(activeQuill) || quillUploadConfigMap.get(quill);
    
    if (!config) return console.warn('[quillUpload] No config found for active editor');

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      try {
        // Build request exactly like CKEditor5 adapter
        const uploadUrl = `${config.apiBaseUrl}${config.attachmentEndpoint}imageUpload`;
        const downloadBase = `${config.apiBaseUrl}${config.attachmentEndpoint}imageDownload/`;

        const form = new FormData();
        // field name expected by backend (same as CK5): fileUpload
        form.append('fileUpload', file, file.name);
        const attachmentJson: any = {
          fkEntityId: config.agendaId,
          fkMeetingId: config.meetingId,
          orgCode: localStorage.getItem('orgCode') || (localStorage as any).orgCode
        };
        if (!attachmentJson.fkEntityId || !attachmentJson.fkMeetingId || !attachmentJson.orgCode) {
          throw new Error('Missing required upload fields');
        }
        form.append('attachment', JSON.stringify(attachmentJson));

        // Use XHR so we can set headers like in CK5 adapter
        const xhr = new XMLHttpRequest();
        const token = localStorage.getItem('token');
        const userName = localStorage.getItem('encLoginName') || '';
        const p = await new Promise<{ ok: boolean; id?: string; message?: string }>((resolve) => {
          xhr.open('POST', uploadUrl, true);
          if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
          if (userName) xhr.setRequestHeader('userName', userName);
          xhr.responseType = 'json';
          xhr.onerror = () => resolve({ ok: false, message: 'Image upload failed' });
          xhr.onload = () => {
            const resp = (xhr.response || {}) as any;
            if (xhr.status >= 200 && xhr.status < 300 && resp.attachmentId) {
              resolve({ ok: true, id: String(resp.attachmentId) });
            } else {
              resolve({ ok: false, message: resp?.message || 'Upload failed' });
            }
          };
          xhr.send(form);
        });

        if (!p.ok || !p.id) throw new Error(p.message || 'Upload failed');
        const url = downloadBase + p.id;

        // Insert the image into the ACTIVE Quill editor at current selection
        const activeEditorForInsert = (window as any).__activeQuillEditor || config.quill;
        const range = activeEditorForInsert.getSelection(true) || { index: activeEditorForInsert.getLength(), length: 0 };
        activeEditorForInsert.insertEmbed(range.index, 'image', url, 'user');
        activeEditorForInsert.setSelection(range.index + 1, 0, 'user');

        try { config.onComplete && config.onComplete(url); } catch {}
      } catch (e) {
        console.error('Image upload failed:', e);
      }
    };
    input.click();
  });
}